/**
 * LEAD-APPOINTMENT-CRM-COHERENCE-V1 (SCOPE 2) -- THE ONE appointment-time picker.
 *
 * ONE-PICKER LAW: the shared server-computed slot grid is the ONLY way to choose
 * an appointment time anywhere (booking AND rescheduling, on every surface). A
 * date input + the server-computed slot grid for that IST date; taken slots are
 * disabled and marked "- booked"; past slots are disabled. There is NO hour/minute
 * selector, NO `datetime-local`, and NO client-side clinic-hours copy -- the
 * window + taken marks come from the SAME booking-rules config the write route
 * enforces (one rule source), fetched through `getDaySlots`.
 *
 * This was extracted verbatim from the cockpit's inline grid
 * (LeadToAppointmentCockpit.tsx STITCH-2 SCOPE 8) so the workbench, the cockpit,
 * and the journey stepper all render the IDENTICAL picker -- duplication removed,
 * not re-implemented.
 *
 * Split by testability (repo idiom -- react-dom/server, no jsdom): the picker is
 * a PURE presentational component (renderToStaticMarkup asserts on its markup);
 * the fetch lives in the `useDaySlots` hook, which the CONTAINER calls (SSR does
 * not run effects, so a static render shows the idle/loading state).
 */

import { useEffect, useState } from 'react';

import {
  fetchDaySlots,
  fetchDaySlotsForClinic,
  type DaySlotsResponse,
} from '@/dashboard/api';
import { AcSelect } from '@/dashboard/ui';
import { formatIstDateDdMmYyyy, formatIstTime } from '@/lib/datetime-ist';

/** The shared slot-grid picker's fetch state (server-computed truth). */
export type DaySlotsState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; data: DaySlotsResponse };

/**
 * Fetch the server-computed slot grid whenever a lead + date are both chosen.
 * Best-effort: a failure degrades to `{ kind: 'error' }` (the surface shows a
 * retry hint), never throws and never fails the parent screen. Idle until both a
 * lead and a date exist.
 *
 * CHANNEL-TO-BOOKED-APPOINTMENT-V1: `clinicId` selects the CLINIC LEG of the
 * SAME endpoint (the FD-20 leg the registered-patient offer already uses) for
 * a lead that has NO clinic yet -- the agent has just chosen the branch, and
 * the lead is not written until they commit to booking. The lead leg is
 * unchanged and still wins whenever the lead HAS a clinic, so a lead-derived
 * grid can never be replaced by a client-named one. ONE hook, ONE endpoint,
 * ONE rule source: the ONE-PICKER LAW holds either way, and the server
 * scope-asserts the clinic on both legs.
 */
export function useDaySlots(
  leadId: number | null,
  date: string,
  clinicId?: number | null,
  retryKey = 0,
): DaySlotsState {
  const [state, setState] = useState<DaySlotsState>({ kind: 'idle' });
  useEffect(() => {
    const byClinic = clinicId !== null && clinicId !== undefined;
    if ((leadId === null && !byClinic) || date === '') {
      setState({ kind: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    const request = byClinic
      ? fetchDaySlotsForClinic(clinicId as number, date)
      : fetchDaySlots(leadId as number, date);
    request
      .then((data) => {
        if (!cancelled) setState({ kind: 'ready', data });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, date, clinicId, retryKey]);
  return state;
}

export interface DaySlotPickerProps {
  /** Frozen Booking pairs Date and Time slot; other shared consumers stay unchanged. */
  readonly compact?: boolean;
  /** Existing appointment only: keep its saved time visible, even when occupied. */
  readonly currentIso?: string;
  /** Same served slots; the approved Reception form uses a single select. */
  presentation?: 'grid' | 'select';
  /** Chosen IST date ('YYYY-MM-DD'); '' until picked. */
  date: string;
  onDateChange: (date: string) => void;
  slots: DaySlotsState;
  /** The chosen slot's UTC instant, or '' when none chosen. */
  selectedIso: string;
  onSelectIso: (iso: string) => void;
  disabled?: boolean;
  /** Namespaces every data-testid so multiple pickers can coexist on a page. */
  idPrefix: string;
}

export interface SlotChoiceGridProps {
  slots: readonly { startIso: string; label: string; taken: boolean; past: boolean }[];
  selectedIso: string;
  onSelectIso: (iso: string) => void;
  disabled?: boolean;
  idPrefix: string;
}

/**
 * The slot-button grid EXTRACTED from the picker body (WALK-IN-COMPLETION-V1)
 * so the walk-in one-motion card can render the SAME server-computed day
 * grid without the date input (multi-day pickers are out of that flow's
 * scope; the Lead-360 workbench stays the multi-day booking home). ONE
 * component, byte-identical markup -- never a second slot rendering.
 */
export function SlotChoiceGrid({
  slots,
  selectedIso,
  onSelectIso,
  disabled = false,
  idPrefix,
}: SlotChoiceGridProps): JSX.Element {
  return (
    <div className="ac-slotpicker__slots">
      {slots.map((slot) => {
        const selected = selectedIso === slot.startIso;
        const unavailable = slot.taken || slot.past;
        return (
          <button
            key={slot.startIso}
            type="button"
            className={
              selected
                ? 'ac-btn ac-btn--primary ac-btn--sm'
                : 'ac-btn ac-btn--secondary ac-btn--sm'
            }
            data-testid={`${idPrefix}-slot`}
            data-taken={slot.taken ? 'yes' : 'no'}
            disabled={disabled || unavailable}
            aria-pressed={selected}
            title={
              slot.taken
                ? 'This slot is already booked'
                : slot.past
                  ? 'This time has passed'
                  : undefined
            }
            onClick={() => onSelectIso(slot.startIso)}
          >
            {slot.label}
            {slot.taken ? ' - booked' : ''}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Pure/presentational slot-grid picker. All interactivity is via handler props so
 * the same component renders in the cockpit, the Lead-360 workbench (book +
 * reschedule), and the journey stepper.
 */
export function DaySlotPicker({
  compact = false,
  currentIso,
  presentation = 'grid',
  date,
  onDateChange,
  slots,
  selectedIso,
  onSelectIso,
  disabled = false,
  idPrefix,
}: DaySlotPickerProps): JSX.Element {
  return (
    <div className={compact ? "ac-slotpicker ac-slotpicker--compact" : "ac-slotpicker"} data-testid={`${idPrefix}-slotpicker`}>
      <div className="clinic-workbench__field ac-slotpicker__field">
        <label htmlFor={`${idPrefix}-date`}>{compact ? 'Date' : 'Date (IST)'}</label>
        <input
          id={`${idPrefix}-date`}
          type="date"
          className="clinic-workbench__input"
          data-testid={`${idPrefix}-date`}
          value={date}
          aria-describedby={compact ? undefined : `${idPrefix}-clinic-date-confirmation`}
          aria-label={compact ? 'Date (IST)' : undefined}
          disabled={disabled}
          onChange={(e) => onDateChange(e.target.value)}
        />
        {/* FD-33b remains: native locale is not the clinic's confirmation.
            Show the shared dd/mm/yyyy value once; a chosen slot adds its IST
            time here instead of repeating the date below the slot grid. */}
        {!compact && <span className="ac-slotpicker__dateecho clinic-workbench__hint"
          id={`${idPrefix}-clinic-date-confirmation`} data-testid={`${idPrefix}-date-ddmmyyyy`}>
          {date !== '' && formatIstDateDdMmYyyy(date) !== '' ? formatIstDateDdMmYyyy(date) : 'dd/mm/yyyy'}
          {selectedIso !== '' ? <span data-testid={`${idPrefix}-selected`}> · {formatIstTime(selectedIso)} IST</span> : null}
        </span>}
      </div>

      {date === '' ? (
        <p className="clinic-workbench__hint" data-testid={`${idPrefix}-slots-idle`}>
          Pick a date to see this clinic&apos;s available slots.
        </p>
      ) : slots.kind === 'loading' ? (
        <p className="clinic-workbench__hint" data-testid={`${idPrefix}-slots-loading`}>
          Loading available slots&hellip;
        </p>
      ) : slots.kind === 'error' ? (
        <p className="clinic-workbench__hint" data-testid={`${idPrefix}-slots-error`}>
          Could not load slots for this date. Try again.
        </p>
      ) : slots.kind === 'ready' ? (
        <div data-testid={`${idPrefix}-slot-grid`}>
          <p className={compact ? "sr-only" : "clinic-workbench__hint"}>
            Clinic hours: between {slots.data.windowText} ({slots.data.slotMinutes}
            -minute slots).
          </p>
          {presentation === 'select' ? <div className="clinic-workbench__field ac-slotpicker__field">
            <label htmlFor={`${idPrefix}-slot-select`}>{compact ? 'Time slot' : 'Slot'}</label>
            <AcSelect id={`${idPrefix}-slot-select`} data-testid={`${idPrefix}-slot-select`}
              value={slots.data.slots.find(slot => Date.parse(slot.startIso) === Date.parse(selectedIso))?.startIso ?? selectedIso} disabled={disabled}
              onChange={event => {
                const value = event.target.value;
                if (value === '' || slots.data.slots.some(slot => slot.startIso === value && !slot.taken && !slot.past)) onSelectIso(value);
              }}>
              <option value="">Choose slot</option>
              {currentIso && selectedIso === currentIso && !slots.data.slots.some(slot => Date.parse(slot.startIso) === Date.parse(currentIso)) ?
                <option value={currentIso} disabled>{formatIstTime(currentIso)}</option> : null}
              {slots.data.slots.map(slot => <option key={slot.startIso} value={slot.startIso} disabled={slot.taken || slot.past}>
                {slot.label}{slot.taken ? ' - booked' : slot.past ? ' - passed' : ''}
              </option>)}
            </AcSelect>
          </div> : (
          <SlotChoiceGrid
            slots={slots.data.slots}
            selectedIso={selectedIso}
            onSelectIso={onSelectIso}
            disabled={disabled}
            idPrefix={idPrefix}
          />
          )}
          {slots.data.slots.every((s) => s.taken || s.past) ? (
            <p className="clinic-workbench__hint" data-testid={`${idPrefix}-slots-full`}>
              No open slots left on this date. Try another day.
            </p>
          ) : null}
        </div>
      ) : null}

    </div>
  );
}

export default DaySlotPicker;
